import React from 'react';
import { Collapse, Tag, Tooltip, Typography } from 'antd';

import { plural, readable, ROLE_COLOURS, signArrow, signWord } from './format';
import { CurationStatus, NewsItem, Sign, Turn } from './types';

const CURATION_TAGS: Partial<
  Record<CurationStatus, { label: string; colour?: string }>
> = {
  pending: { label: 'curating…', colour: 'processing' },
  needs_review: { label: 'curated: needs review', colour: 'gold' },
  failed: { label: 'curation failed', colour: 'red' },
};

const roleCounts = (turn: Turn) =>
  turn.news.reduce(
    (counts, item) => ({ ...counts, [item.role]: counts[item.role] + 1 }),
    { driver: 0, context: 0, counter: 0 },
  );

/** "T1 · 2022-09-17→2022-09-23 · 8 items (4 drivers, 3 context, 1 counter)" */
export const turnHeader = (turn: Turn) => {
  const counts = roleCounts(turn);
  return (
    `T${turn.index} · ${turn.start}→${turn.end} · ` +
    `${plural(turn.news.length, 'item')} (${plural(counts.driver, 'driver')}, ` +
    `${counts.context} context, ${counts.counter} counter)`
  );
};

const NewsRow: React.FC<{ item: NewsItem }> = ({ item }) => {
  const curated = item.curation?.headline;
  const curationTag = item.curation
    ? CURATION_TAGS[item.curation.status]
    : null;
  const signs = Object.entries(item.signs || {}) as Array<[string, Sign]>;
  return (
    <li
      className="blueprint-builder__news-item"
      data-testid={`news-${item.id}`}
    >
      <div className="blueprint-builder__news-meta">
        <Tag color={ROLE_COLOURS[item.role]}>{item.role}</Tag>
        <Typography.Text type="secondary">
          {item.day || String(item.timestamp || '').slice(0, 10)}
        </Typography.Text>
        {curationTag && (
          <Tooltip title={item.curation?.reason || undefined}>
            <Tag color={curationTag.colour}>{curationTag.label}</Tag>
          </Tooltip>
        )}
      </div>
      <div className="blueprint-builder__news-headline">
        {curated || item.headline}
      </div>
      {curated && (
        <div className="blueprint-builder__news-was">was: {item.headline}</div>
      )}
      {(item.concepts.length > 0 || signs.length > 0) && (
        <div className="blueprint-builder__news-tags">
          {item.concepts.map(concept => (
            <Tag key={concept} className="blueprint-builder__concept">
              {readable(concept)}
            </Tag>
          ))}
          {signs.map(([code, sign]) => (
            <Tooltip
              key={code}
              title={`Expected push on ${code}: ${signWord(sign)}`}
            >
              <span
                className={`blueprint-builder__sign blueprint-builder__sign--${
                  sign > 0 ? 'up' : sign < 0 ? 'down' : 'flat'
                }`}
                aria-label={`${code} ${signWord(sign)}`}
              >
                {code} {signArrow(sign)}
              </span>
            </Tooltip>
          ))}
        </div>
      )}
      {item.reasons.length > 0 && (
        <div className="blueprint-builder__news-reasons">
          why: {item.reasons.join(' · ')}
        </div>
      )}
    </li>
  );
};

/**
 * The news each turn would serve, one panel per turn. The curated headline
 * (when there is one) is the main text with the original under it; signs are
 * the headline's expected push per instrument, judged before the fact.
 */
const NewsByTurn: React.FC<{ turns: Turn[] }> = ({ turns }) => {
  if (!turns.length) {
    return (
      <Typography.Text type="secondary">
        No turns in this preview.
      </Typography.Text>
    );
  }
  return (
    <Collapse
      size="small"
      className="blueprint-builder__news"
      items={turns.map(turn => ({
        key: String(turn.index),
        label: turnHeader(turn),
        extra: turn.thin ? (
          <Tooltip
            title={`${turn.news.length} of ${turn.quota} wanted; ${turn.available} available`}
          >
            <Tag color="warning">thin news</Tag>
          </Tooltip>
        ) : null,
        children: turn.news.length ? (
          <ul className="blueprint-builder__news-list">
            {turn.news.map(item => (
              <NewsRow key={item.id} item={item} />
            ))}
          </ul>
        ) : (
          <Typography.Text type="secondary">
            No news selected for this turn.
          </Typography.Text>
        ),
      }))}
    />
  );
};

export default NewsByTurn;
